import { useEffect, ReactElement, cloneElement } from 'react';

import { PlaceholderItem } from '../../hooks/usePlaceholderItems';

export interface ItemsLoaderResult<Item> {
    items?: Item[];
    isFetched?: boolean;
}

export type ItemsLoaderHook<Item = unknown> = (
    query?: Record<string, unknown> | null,
    opts?: Record<string, unknown>,
) => ItemsLoaderResult<Item>;

export interface ItemsLoaderProps<Item = unknown> {
    hook: ItemsLoaderHook<Item>;
    query?: Record<string, unknown> | null;
    suspense?: boolean;
    disabled?: boolean;
    initialData?: Item[] | { data: Item[] } | null;
    placeholderData?: PlaceholderItem[] | { data: PlaceholderItem[] } | null;
    children: ReactElement;
    onLoaded?: ((items: Item[]) => void) | null;
}

function ItemsLoader<Item = unknown>({
    hook,
    query = null,
    initialData = null,
    placeholderData = null,
    suspense = false,
    disabled = false,
    children,
    onLoaded = null,
}: ItemsLoaderProps<Item>) {
    const loadResult = hook(query, {
        suspense,
        enabled: !disabled,
        ...(initialData !== null
            ? {
                  initialData,
              }
            : null),
        placeholderData,
    });

    const { items, isFetched } = loadResult || {};

    useEffect(() => {
        if (isFetched && items !== null && onLoaded !== null) {
            onLoaded(items);
        }
    }, [items, isFetched]);

    return cloneElement(children, loadResult);
}

export default ItemsLoader;
